import type { ReactNode } from 'react';
import { motion } from 'framer-motion';
import Logo from '@/components/branding/Logo';

const STEPS = [
  { label: 'Your name', hint: 'How we address you' },
  { label: 'Interview date', hint: 'Pace your prep' },
  { label: 'English level', hint: 'Tune feedback' },
  { label: 'Focus areas', hint: 'Where to push' },
  { label: 'Your plan', hint: 'A quick recap' },
  { label: 'Try a question', hint: 'Sample the experience' },
  { label: 'Choose your plan', hint: 'Pick a plan or continue free' },
];

interface OnboardingShellProps {
  step: number;
  eyebrow?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
}

export default function OnboardingShell({
  step,
  eyebrow,
  title,
  subtitle,
  children,
}: OnboardingShellProps) {
  const stepIndex = step - 1;

  return (
    <div className="min-h-screen bg-cream lg:grid lg:grid-cols-[420px_1fr] xl:grid-cols-[480px_1fr]">
      {/* Desktop side rail */}
      <aside className="hidden lg:flex relative overflow-hidden bg-gradient-to-b from-cream-warm to-cream-light border-r border-border">
        <div className="absolute -top-32 -right-24 h-80 w-80 rounded-full bg-primary/10 blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 h-72 w-72 rounded-full bg-accent/15 blur-3xl pointer-events-none" />

        <div className="relative z-10 flex flex-col w-full px-12 py-16 xl:px-16">
          <div className="mb-16">
            <Logo variant="horizontal" size="md" />
          </div>

          <div className="mb-10">
            <div className="font-sans text-xs text-text-muted uppercase tracking-widest mb-2">
              Step {step} of {STEPS.length}
            </div>
            <div className="font-display italic font-light text-3xl xl:text-4xl text-text-primary leading-tight">
              Setting up your study plan.
            </div>
          </div>

          <ol className="space-y-1">
            {STEPS.map((s, i) => {
              const status =
                i < stepIndex ? 'done' : i === stepIndex ? 'current' : 'upcoming';
              return (
                <li key={s.label} className="flex items-start gap-4 py-2">
                  <div className="relative flex flex-col items-center pt-0.5">
                    <div
                      className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-sans font-semibold transition-colors ${
                        status === 'done'
                          ? 'bg-success text-white'
                          : status === 'current'
                            ? 'bg-primary text-white ring-4 ring-primary/15'
                            : 'bg-card border border-border text-text-muted'
                      }`}
                    >
                      {status === 'done' ? '✓' : i + 1}
                    </div>
                    {i < STEPS.length - 1 && (
                      <div
                        className={`w-px h-7 mt-1 ${
                          status === 'done' ? 'bg-success/40' : 'bg-border'
                        }`}
                      />
                    )}
                  </div>
                  <div className="pb-2">
                    <div
                      className={`font-sans font-medium text-sm leading-tight ${
                        status === 'upcoming'
                          ? 'text-text-muted'
                          : 'text-text-primary'
                      }`}
                    >
                      {s.label}
                    </div>
                    {status === 'current' && (
                      <div className="font-sans text-xs text-text-secondary mt-0.5">
                        {s.hint}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      </aside>

      {/* Right column — form */}
      <main className="flex flex-col items-center justify-center min-h-screen px-6 py-8 md:px-12 md:py-12 lg:py-20 lg:px-16 xl:px-24">
        <div className="w-full max-w-md lg:max-w-lg">
          {/* Mobile-only progress bar */}
          <div className="flex gap-1.5 mb-8 lg:hidden">
            {STEPS.map((_, i) => (
              <div
                key={i}
                className={`h-1 flex-1 rounded-full ${
                  i < stepIndex
                    ? 'bg-success'
                    : i === stepIndex
                      ? 'bg-primary'
                      : 'bg-border'
                }`}
              />
            ))}
          </div>

          {eyebrow && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="font-sans text-xs uppercase tracking-[0.18em] font-medium text-text-secondary mb-3"
            >
              {eyebrow}
            </motion.div>
          )}

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="font-display font-semibold text-[40px] md:text-5xl leading-tight text-text-primary mb-3"
          >
            {title}
          </motion.h1>

          {subtitle && (
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="font-sans text-base md:text-lg text-text-secondary mb-8"
            >
              {subtitle}
            </motion.p>
          )}

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {children}
          </motion.div>
        </div>
      </main>
    </div>
  );
}
